<script setup>
import TextInput from "@/Components/Form/TextInput.vue";
import Checkbox from "@/Components/Form/Checkbox.vue";
import {Head, Link, useForm, usePage} from "@inertiajs/vue3";
import {onMounted, ref, defineProps, computed} from "vue";
import {trans, wTrans} from "laravel-vue-i18n";
import DefaultModal from "@/Components/DefaultModal.vue";
import ErrorMessage from "@/Components/Form/ErrorMessage.vue";

const pageTitle = wTrans('auth.login.title')

const page = usePage();
const errors = computed(() => page.props.errors);

const props = defineProps({
    rules: {
        type: String
    },
    status: {
        type: String,
    },
});

const form = useForm({
    email: '',
    password: '',
    rememberMe: true,
});

const logIn = () => {
    form.post(route('login'), {
        onFinish: () => form.reset('password'),
    });
};

const isDemoDialogOpen = ref(false);

onMounted(() => {
    isDemoDialogOpen.value = true
})
</script>

<template>
    <Head :title="pageTitle" />
    <section class="loginPage">
        <div class="container loginPage-container">
            <h1 v-text="pageTitle" />
            <form>
                <ErrorMessage
                    v-if="errors.email"
                    :message="errors.email"
                />
                <TextInput
                    for="email"
                    :placeholder="trans('auth.login.your-email-address')"
                    v-model="form.email"
                    required
                    type="email"
                    autocomplete="email"
                />
                <TextInput
                    for="password"
                    :placeholder="trans('shared.common.password')"
                    type="password"
                    v-model="form.password"
                    autocomplete="password"
                    required
                />
                <Checkbox
                    :label="trans('auth.login.remember-me')"
                    v-model="form.rememberMe"
                    :true-value="true"
                    :false-value="false"
                />
                <button
                    class="button -is-primary"
                    :class="{ '--isProcessing': form.processing }"
                    :disabled="form.processing"
                    @click.prevent="logIn"
                    v-text="trans('auth.login.title')"
                />
                <Link
                    :href="route('password.request')"
                    class="loginPage-forgotPassword text-center"
                >
                    {{ trans('auth.forgot-password.title') }} ?
                </Link>
                <div class="loginPage-rules">
                    {{ trans('auth.reset-password.reminder-of-password-rules') }} : {{ rules }}
                </div>
            </form>
        </div>
    </section>

    <DefaultModal
        v-model:open="isDemoDialogOpen"
    >
        <template #title>
            {{ trans('auth.login.demo.title') }}
        </template>

        <template #body>
            <div v-html="trans('auth.login.demo.description')" />
        </template>
    </DefaultModal>
</template>

<style lang="scss" scoped>
@use '~@/base/variables' as *;

.loginPage {
    &-container {
        display: flex;
        align-items: center;
        flex-direction: column;
    }
    form {
        width: 360px;
        display: flex;
        flex-direction: column;
        align-content: center;
        gap: 15px;
    }
    &-forgotPassword {
        color: $blue800;
    }
    &-rules {
        color: $gray500;
    }
}
</style>
